import React from "react";
import { FiBriefcase, FiLayers, FiTool, FiUsers } from "react-icons/fi";
import {
  Code,
  Note,
  Section,
  Table,
  Tbody,
  TD,
  Thead,
} from "../../../../pages/KnowledgeCenterPage/ApiDocs/primitives";
import {
  Arrow,
  CheckItWorked,
  Diagram,
  Flow,
  GuideLink,
  Lead,
  List,
  Node,
  NodeItem,
  NoWrap,
  StatusPill,
  Step,
  Steps,
} from "../primitives";

/* ------------------------------------------------------------------ client */

/** The four pieces of a client and how they connect. */
export const ClientDiagram: React.FC = () => (
  <Diagram
    label="The pieces of a client"
    minWidth={820}
    caption="A client is not one object. It is a company, an asset group, a role and the users in that role."
  >
    <div
      className="grid items-center gap-y-5"
      style={{
        gridTemplateColumns:
          "minmax(120px,1fr) minmax(76px,100px) minmax(120px,1fr) minmax(68px,90px) minmax(124px,1fr) minmax(76px,100px) minmax(210px,1.5fr)",
      }}
    >
      <div className="row-span-2">
        <Node title="Company" caption="Company tab" icon={FiBriefcase} tone="company">
          <NodeItem name="Client Domain">the email domain the users have</NodeItem>
        </Node>
      </div>
      <div className="row-span-2 self-stretch">
        <Arrow label="picked on each user" />
      </div>
      <div className="row-span-2">
        <Node title="Users" caption="User Setup tab" icon={FiUsers} tone="people">
          <NodeItem name="Company">the client</NodeItem>
        </Node>
      </div>
      <div className="row-span-2 self-stretch">
        <Arrow label="members of" />
      </div>
      <div className="row-span-2 self-stretch py-2">
        <Node
          title="Role"
          caption="Role tab, domain amp"
          icon={FiUsers}
          tone="people"
          className="flex h-full flex-col justify-center"
        >
          <NodeItem name={<Code>&lt;client&gt;-read-all</Code>} />
        </Node>
      </div>

      <div className="self-stretch">
        <Arrow label="holds at read" />
      </div>
      <Node title="Endpoint groups" caption="Endpoints Group tab" icon={FiLayers} tone="endpoint">
        <NodeItem name={<Code>amp_external_client_base</Code>}>the client-facing pages and their APIs</NodeItem>
        <NodeItem name={<Code>reference_api</Code>}>reference data</NodeItem>
        <NodeItem name={<Code>&lt;client&gt;_..._blob_access</Code>}>the client's FileCenter folders</NodeItem>
      </Node>

      <div className="self-stretch">
        <Arrow label="holds" />
      </div>
      <Node title="Asset group" caption="Asset Group tab, domain amp" icon={FiLayers} tone="asset">
        <NodeItem name={<Code>&lt;Client&gt;-Read</Code>}>the client's assets</NodeItem>
      </Node>
    </div>
  </Diagram>
);

export const OnboardingClient: React.FC = () => (
  <div className="space-y-8">
    <Lead>Use this when a new client company needs AMP access to its own assets.</Lead>

    <ClientDiagram />

    <Section title="What a client role holds">
      <p>Copy the pattern the existing clients use. Every endpoint group is held at read.</p>
      <Table minWidth={720}>
        <Thead cols={["Piece", "Name", "Notes"]} />
        <Tbody>
          <tr>
            <td className={TD}>Role</td>
            <td className={TD}>
              <NoWrap><Code>&lt;client&gt;-read</Code>,</NoWrap> <NoWrap><Code>&lt;client&gt;-read-all</Code></NoWrap> or{" "}
              <NoWrap><Code>&lt;client&gt;-all</Code></NoWrap>
            </td>
            <td className={TD}>
              Domain amp. Lowercase, words joined by hyphens, such as <Code>esvolta-read-all</Code> or{" "}
              <Code>pluspower-all</Code>.
            </td>
          </tr>
          <tr>
            <td className={TD}>Client-facing endpoint group</td>
            <td className={TD}><Code>amp_external_client_base</Code></td>
            <td className={TD}>
              The baseline client access: the Landing, Lineview, Asset Dashboard and File Center pages plus their
              core APIs. BETM staff roles map it too.
            </td>
          </tr>
          <tr>
            <td className={TD}>Reference data</td>
            <td className={TD}><Code>reference_api</Code></td>
            <td className={TD}>Held by nearly every role.</td>
          </tr>
          <tr>
            <td className={TD}>FileCenter group</td>
            <td className={TD}><Code>&lt;client&gt;_..._blob_access</Code></td>
            <td className={TD}>
              The folders of the client's assets, such as <Code>esvolta_all_blob_access</Code>.
            </td>
          </tr>
          <tr>
            <td className={TD}>Asset group</td>
            <td className={TD}>Exactly one, domain amp</td>
            <td className={TD}>
              The client's assets, such as <Code>esVolta-All</Code>, <Code>CIM-Read</Code> or{" "}
              <Code>PlusPower-All</Code>.
            </td>
          </tr>
          <tr>
            <td className={TD}>Users</td>
            <td className={TD}>The client's people</td>
            <td className={TD}>Each carries the client's company. Sometimes one API service principal too.</td>
          </tr>
        </Tbody>
      </Table>
      <Note>
        Client roles are not all the same. CIM's role also holds <Code>market_fundamentals</Code>, and Plus
        Power's holds a genbids write group. Names also differ in case and wording between a role, its asset
        group and its FileCenter group. Open a similar client in Access Explorer, and search before you create
        anything.
      </Note>
    </Section>

    <Section title="Steps">
      <p>Do them in this order. The Role tab needs at least one user, so the users come before the role.</p>
      <Steps>
        <Step title="Create the company" tab="Assets > Company" check="The company shows in the Company grid.">
          <p>
            Click New and enter the Company Name. Under Add Client Domain, type the client's email domain and
            click Add. Click Create and confirm.
          </p>
          <p className="text-gray-500">
            More: <GuideLink to="onboarding-company">Onboarding a company</GuideLink>.
          </p>
        </Step>
        <Step
          title="Create the asset group"
          tab="Entitlements > Asset Group"
          check="The group shows in the grid with the assets you picked."
        >
          <p>
            Click New. Asset Group Name: the client's name with -Read or -All. Company: the client. Domain: amp.
            Select Assets: the client's assets. Click Create and confirm.
          </p>
          <p className="text-gray-500">
            More: <GuideLink to="onboarding-asset-group">Onboarding an asset group</GuideLink>.
          </p>
        </Step>
        <Step
          title="Check the client's FileCenter group"
          tab="Entitlements > Endpoints Group"
          check="The group lists folder objects for each of the client's assets."
        >
          <p>
            The client's folder access comes from an endpoint group of BLOB_FOLDER objects, named like{" "}
            <Code>&lt;client&gt;_read_blob_access</Code>. Find it, or create it: POG Name, Domain amp, and in
            Select Permission Objects the folder objects of the client's assets.
          </p>
          <p className="text-gray-500">
            More: <GuideLink to="filecenter-folders">FileCenter folders</GuideLink>.
          </p>
        </Step>
        <Step
          title="Invite the users"
          tab="Entitlements > User Setup"
          check={<>User Setup says <Code>User "First Last" successfully invited.</Code></>}
        >
          <p>
            Click New. Fill Email, Company (the client), First Name, Last Name and Enabled. Leave Roles empty: the
            role doesn't exist yet. Click Create.
          </p>
          <p>
            User Setup finds the person in the external tenant, or invites them by email. They don't need to
            exist first or to have signed in.
          </p>
          <p className="text-gray-500">
            More: <GuideLink to="onboarding-user">Onboarding a user</GuideLink>.
          </p>
        </Step>
        <Step title="Create the role" tab="Entitlements > Role" check="The role shows in the grid with its users.">
          <p>Click New and fill in:</p>
          <List>
            <li>Role Name: lowercase with hyphens, such as <Code>&lt;client&gt;-read-all</Code>.</li>
            <li>Domain: amp. You can't change it later.</li>
            <li>Users: the users from step 4. At least one is required.</li>
            <li>Asset Groups (Collections): the group from step 2.</li>
            <li>
              Permission Object Groups: <Code>amp_external_client_base</Code>, <Code>reference_api</Code> and the
              FileCenter group from step 3.
            </li>
          </List>
          <p>Click Create and confirm. The Role tab maps every endpoint group at read. It can't set write or admin.</p>
        </Step>
      </Steps>
    </Section>

    <p>
      Later, to add a person to this client, invite them with the client's company and tick the client's role
      in Roles. Mobile app access is separate: see <GuideLink to="mobile-users">Mobile users</GuideLink>.
    </p>

    <CheckItWorked>
      <li>
        Access Explorer, Role: pick the new role. Members lists the users. Pages shows <Code>/landing</Code>,{" "}
        <Code>/lineview</Code>, <Code>/asset-dashboard</Code> and <Code>/filecenter</Code>. Asset groups: click
        the group to list its assets.
      </li>
      <li>Switch to User and pick one of the users. Roles shows the new role.</li>
      <li>
        Click Refresh entitlements and allow up to 15 minutes, or click Refresh access on one user to apply it
        for them now (see <GuideLink to="when-changes-apply">When changes apply</GuideLink>). Then ask a user to
        sign in. They should see Landing, Lineview, Asset Dashboard and File Center, with only the client's
        assets.
      </li>
    </CheckItWorked>
  </div>
);

/* ----------------------------------------------------------------- company */

export const OnboardingCompany: React.FC = () => (
  <div className="space-y-8">
    <Lead>Use this before you invite a new client's users, or when a client starts using a new email domain.</Lead>

    <Section title="What a company does">
      <p>
        A company is an asset-metadata record: a name, optional owner-asset links and optional email domains.
        It grants no access by itself. It feeds two things:
      </p>
      <List>
        <li>The Company dropdown in User Setup and Asset Group.</li>
        <li>
          The email-domain list that User Setup checks before it invites anyone. Any company's domains count,
          not only the company you pick for the user.
        </li>
      </List>
    </Section>

    <Section title="Steps">
      <Steps>
        <Step title="Start a new company" tab="Assets > Company">
          <p>Click New. Company Name is the only required field.</p>
        </Step>
        <Step title="Add owner assets, if any">
          <p>
            Optional: Owner Name, and Asset Details (pick an asset and its percent ownership, then add it).
          </p>
        </Step>
        <Step title="Add the client domains">
          <p>
            Under Add Client Domain, type a domain, such as the part after @ in the client's email addresses, and
            click Add. You can add up to 100. In edit mode the trash icon removes one.
          </p>
        </Step>
        <Step title="Save" check="The company shows in the Company grid straight away.">
          <p>Click Create and confirm. To change a company later, select it, click Edit, then Save.</p>
        </Step>
      </Steps>
    </Section>

    <Section title="Watch out for">
      <List>
        <li>
          Search the grid first. When you save a company with domains, assets or an owner name, a company with
          exactly the same name is reused, not created again.
        </li>
        <li>Saving an owner link for an asset the company already owns fails.</li>
        <li>
          A new domain can be refused for up to 3 hours. The entitlements service keeps the domain list for 3
          hours, so User Setup can still say <Code>&lt;domain&gt; not in Domain list</Code> right after you
          added it. Try again later.
        </li>
      </List>
    </Section>

    <CheckItWorked>
      <li>The company shows in the Company grid.</li>
      <li>Reload User Setup or Asset Group: the company is in their Company dropdown.</li>
      <li>
        Invite the first user (<GuideLink to="onboarding-user">Onboarding a user</GuideLink>). The invite goes
        through once the domain is accepted.
      </li>
    </CheckItWorked>
  </div>
);

/* ------------------------------------------------------------- asset group */

export const OnboardingAssetGroup: React.FC = () => (
  <div className="space-y-8">
    <Lead>Use this when a role should see a new set of assets, such as a new client's assets.</Lead>

    <Section title="What an asset group is">
      <p>
        An asset group (also called a collection) is a named list of assets in one domain. It does nothing
        until a role holds it.
      </p>
      <Table minWidth={680}>
        <Thead cols={["Kind", "Domain", "Pattern", "Examples"]} />
        <Tbody>
          <tr>
            <td className={TD}>Client</td>
            <td className={TD}>amp</td>
            <td className={TD}>One per client, ending in -Read, -All or -READ-ALL</td>
            <td className={TD}><Code>esVolta-All</Code>, <Code>CIM-Read</Code>, <Code>PlusPower-All</Code></td>
          </tr>
          <tr>
            <td className={TD}>BETM</td>
            <td className={TD}>amp</td>
            <td className={TD}>Internal groups</td>
            <td className={TD}><Code>BETM-All-Assets</Code>, <Code>BETM-Internal-Asset</Code></td>
          </tr>
          <tr>
            <td className={TD}>Mobile</td>
            <td className={TD}>mobile</td>
            <td className={TD}>One per asset</td>
            <td className={TD}><Code>MobileApp_&lt;Asset&gt;_Read</Code></td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Steps">
      <Steps>
        <Step
          title="Create the group"
          tab="Entitlements > Asset Group"
          check="The group shows in the grid with its assets."
        >
          <p>Click New and fill in:</p>
          <List>
            <li>Asset Group Name: required. Search first: existing names differ in case.</li>
            <li>Company: optional. It is for reference only. Access checks don't read it.</li>
            <li>
              Domain: amp for AMP, mobile for the mobile app. You can't change it later, because role mappings
              are stored by domain.
            </li>
            <li>Select Assets: at least one.</li>
          </List>
          <p>
            Click Create and confirm "Are you sure you want to create this asset group?". "Missing Group Name" or
            "No Assets Selected" means a required field is empty.
          </p>
        </Step>
        <Step title="Set folder permissions, if the client uses File Center" tab="Entitlements > Asset Group">
          <p>
            In Select Assets, click the row arrow on an asset (Load folder permissions). Asset Folder Permissions
            lists its folders under Read and Write. Turning a folder on creates its BLOB_FOLDER object. Turning it
            off deletes it.
          </p>
          <p>
            A new folder object is added only to FileCenter groups that already hold folders for that asset. So
            the client's <Code>_blob_access</Code> group must already contain the asset. See{" "}
            <GuideLink to="filecenter-folders">FileCenter folders</GuideLink>.
          </p>
        </Step>
        <Step title="Give the group to a role" tab="Entitlements > Role">
          <p>
            Edit the role, pick the group in Asset Groups (Collections) and click Save. A role lists only the
            asset groups of its own domain.
          </p>
        </Step>
      </Steps>
      <p>To change a group's assets later, select it, click Edit, add or remove assets and click Save.</p>
      <Note>
        Adding an asset to a client's group doesn't add its folders to the client's FileCenter group. Check
        both.
      </Note>
    </Section>

    <CheckItWorked>
      <li>
        Access Explorer, Role: pick a role that holds the group. Open Asset groups and click the group to list
        its assets. An asset you can't see yourself shows "Not in your asset list".
      </li>
      <li>Pick one of the role's users in User mode. The group shows under Asset groups.</li>
      <li>
        Users see the assets after Refresh entitlements or the hourly refresh at :06 has run. See{" "}
        <GuideLink to="when-changes-apply">When changes apply</GuideLink>.
      </li>
    </CheckItWorked>
  </div>
);

/* -------------------------------------------------------------------- user */

export const OnboardingUser: React.FC = () => (
  <div className="space-y-8">
    <Lead>
      Use this when a person needs to sign in to AMP or the mobile app, or an API client needs credentials.
    </Lead>

    <Note tone="info">
      Before you start: the person's email domain must be on a company's Client Domain list (any company), and
      the company you will pick must exist. See <GuideLink to="onboarding-company">Onboarding a company</GuideLink>.
    </Note>

    <Section title="What Create does">
      <Flow
        label="What Create does"
        steps={[
          {
            kicker: "1",
            title: "Checks the email domain",
            detail: <>Not on any company's list: 403 <Code>&lt;domain&gt; not in Domain list</Code>.</>,
          },
          {
            kicker: "2",
            title: "Finds or invites the person",
            detail: "Looks them up in the external tenant. If they're not there, invites them by email.",
          },
          {
            kicker: "3",
            title: "Saves the user",
            detail: "Only once an Entra object id comes back. No id: 502 and nothing is saved.",
          },
          {
            kicker: "4",
            title: "Adds the roles you picked",
            detail: "One role mapping per role ticked.",
          },
        ]}
      />
      <p>
        The person doesn't need to exist in the tenant first or to have signed in. Saving an email that already
        exists updates that user. It doesn't make a duplicate.
      </p>
    </Section>

    <Section title="Steps">
      <Steps>
        <Step title="Open a new user form" tab="Entitlements > User Setup">
          <p>Click New. Keep the form on users: the Service Principal button switches it to API clients.</p>
        </Step>
        <Step title="Fill the required fields">
          <p>Email, Company, First Name, Last Name and Enabled (Y). Each has a red asterisk.</p>
        </Step>
        <Step title="Pick roles, or leave them for later">
          <p>
            Roles lists amp and mobile roles only. For an existing client, tick the client's role. For a new
            client, leave it empty and add the users when you create the role.
          </p>
          <p>The Assets and Folders panes are previews. They fill in after you save.</p>
        </Step>
        <Step
          title="Click Create"
          check={<>User Setup says <Code>User "First Last" successfully invited.</Code></>}
        />
      </Steps>
      <p>
        To change an existing user's roles, select them, click Edit, tick or untick Roles and click Save.
      </p>
    </Section>

    <Section title="API clients (service principals)">
      <p>
        Click Service Principal to switch the form. Client Name and Company are required. Click Create. The
        client secret shows once, so copy it then. The login name shows as{" "}
        <Code>BETMExternal-API-&lt;name&gt;-&lt;ENV&gt;</Code>.
      </p>
    </Section>

    <Section title="Watch out for">
      <List>
        <li>
          403 <Code>&lt;domain&gt; not in Domain list</Code>: add the domain to a company first. A domain added in
          the last 3 hours can still be refused.
        </li>
        <li>
          502 on Create: no Entra object id came back, often an expired Entra credential. Nothing was saved, so
          it is safe to retry once that is fixed.
        </li>
        <li>A disabled user keeps their roles but gets no pages, APIs or asset groups.</li>
        <li>User Setup lists external users only, and Roles hides roles of other domains (jupiter, ai).</li>
      </List>
    </Section>

    <CheckItWorked>
      <li>Access Explorer, User: pick the person. Roles comes first, then Pages, APIs and Asset groups.</li>
      <li>In User Setup, the selected user's Assets and Folders panes list what they can reach.</li>
      <li>
        A person new to the external tenant accepts the invitation, then signs in. Access shows after Refresh
        entitlements has run, or straight away after Refresh access on the user. See{" "}
        <GuideLink to="when-changes-apply">When changes apply</GuideLink>.
      </li>
    </CheckItWorked>
  </div>
);

/* ------------------------------------------------------------------- asset */

const FakeButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex shrink-0 cursor-default items-center whitespace-nowrap rounded-lg bg-[#a6a9ac] px-3 py-1 text-xs font-medium text-white">
    {children}
  </span>
);

const CHECKLIST: {
  status: "done" | "missing" | "warn" | "choice";
  label: string;
  help: string;
  button?: string;
}[] = [
  { status: "done", label: "Asset record exists", help: "Created in Assets > Asset." },
  {
    status: "missing",
    label: "Not visible to users",
    help: "Not in the BETM-All-Assets group, so users can't see it.",
    button: "Add to BETM-All-Assets",
  },
  {
    status: "missing",
    label: "FileCenter all-folders permissions missing",
    help: "The Logic App cannot write to this asset's FileCenter folders.",
    button: "Grant folders",
  },
  { status: "warn", label: "No resources defined yet", help: "Add them on the Resource tab.", button: "Open Resources" },
  {
    status: "choice",
    label: "Outage reporting: not in the Outage-Assets group",
    help: "Add it only if users report outages for this asset.",
    button: "Add to Outage-Assets",
  },
];

/** A copy of the Onboarding checklist for a brand-new asset. */
export const ChecklistPreview: React.FC = () => (
  <figure aria-label="Onboarding checklist for a new asset" className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <div className="mb-2 flex items-center justify-between gap-4">
      <span className="font-medium text-gray-700">Onboarding checklist</span>
      <FakeButton>
        <FiTool className="mr-1.5" aria-hidden="true" />
        Fix all
      </FakeButton>
    </div>
    <ul className="divide-y rounded-lg border bg-white">
      {CHECKLIST.map((row) => (
        <li key={row.label} className="flex items-center gap-4 px-4 py-2.5">
          <StatusPill status={row.status} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-5 text-gray-800">{row.label}</p>
            <p className="text-xs leading-4 text-gray-500">{row.help}</p>
          </div>
          {row.button && <FakeButton>{row.button}</FakeButton>}
        </li>
      ))}
    </ul>
    <figcaption className="mt-2 text-xs leading-5 text-gray-500">
      A new asset before Fix all. Fix all handles the two Missing rows. Outage and resources stay with you.
    </figcaption>
  </figure>
);

export const OnboardingAnAsset: React.FC = () => (
  <div className="space-y-8">
    <Lead>
      Use this when a new asset is set up, or an asset doesn't show for users or can't take Logic App uploads.
    </Lead>

    <Flow
      label="Onboarding an asset"
      steps={[
        { kicker: "1", title: "New asset", detail: "Create the record on the Onboarding tab, or pick the asset." },
        { kicker: "2", title: "Fix all", detail: "BETM-All-Assets, then FileCenter folders.", tone: "accent" },
        { kicker: "3", title: "Outage", detail: "Your choice.", tone: "muted" },
        { kicker: "4", title: "Resources", detail: "On the Resource tab." },
        { kicker: "5", title: "Apply", detail: "Click Refresh assets, then Refresh entitlements." },
      ]}
    />

    <ChecklistPreview />

    <Section title="Steps">
      <Steps>
        <Step title="Create or pick the asset" tab="Assets > Onboarding">
          <p>
            Click New asset to create the record here: fill in the asset form, click Create and confirm. The new
            asset is selected and its checklist opens. For an asset that already exists, pick it in the list on the left.
          </p>
        </Step>
        <Step title="Click Fix all">
          <p>
            Fix all runs only the missing steps, in order: it adds the asset to <Code>BETM-All-Assets</Code>,
            grants its FileCenter folders, then reloads the list and the checklist. It shows only
            when one of those two rows is Missing.
          </p>
          <p>
            If a step fails, it stops and says which one ("Fix all stopped while ..."). The checklist then shows
            what got done. Each row also has its own button: Add to BETM-All-Assets and Grant folders.
          </p>
        </Step>
        <Step title="Decide on outage reporting">
          <p>
            Fix all leaves this to you. Click Add to Outage-Assets only if users report outages for this asset.
            Keep upload-only accounts out. See <GuideLink to="outage-assets">Outage assets</GuideLink>.
          </p>
        </Step>
        <Step title="Add its resources" tab="Assets > Resource">
          <p>
            Open Resources takes you to the Resource tab. Add the resources there. Back on Onboarding, the row
            shows how many are defined.
          </p>
        </Step>
        <Step title="Refresh the AMP pages" tab="Assets">
          <p>
            Click Refresh assets at the top right of the Assets tab. The AMP pages then show the new asset and its
            resources. It can take up to 15 minutes. Otherwise the hourly asset refresh picks it up.
          </p>
        </Step>
        <Step title="Apply it for everyone" tab="Entitlements">
          <p>
            Click Refresh entitlements at the top right of the Entitlements tab. It can take up to 15 minutes.
            Otherwise the hourly refresh at :06 applies it.
          </p>
        </Step>
      </Steps>
      <Note tone="info">
        New asset, Fix all and the row buttons that change data show for admins only. The IT group sees only the
        checklist.
      </Note>
    </Section>

    <CheckItWorked>
      <li>
        After Fix all, the visible and FileCenter rows read Done.
      </li>
      <li>
        The AMP pages show the asset and its resources after Refresh assets or the hourly asset refresh has run.
      </li>
      <li>
        A new asset stays in your list until you reload the page. After a reload it is listed only once Refresh
        entitlements has run.
      </li>
      <li>
        If the resources row says "Can't read resources", your access is stale. An admin can click Refresh
        entitlements on the Entitlements tab. That can take up to 15 minutes. Otherwise the hourly refresh at
        :06 picks it up.
      </li>
      <li>
        Other users see the asset after Refresh entitlements or the hourly refresh at :06 has run. See{" "}
        <GuideLink to="when-changes-apply">When changes apply</GuideLink>.
      </li>
    </CheckItWorked>
  </div>
);
